import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';
import Pagination from '../../../components/common/Pagination';
import {
  getReportsSummary,
  getPendingJobsReport,
  getPendingCustomersReport,
} from '../../../services/reportsService';
import { getApiErrorMessage } from '../../../services/http';

const Reports = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState({ salesTotal: 0, pendingJobs: 0, pendingBalance: 0 });
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingCustomers, setPendingCustomers] = useState([]);

  // Pagination for pending jobs
  const [jobsCurrentPage, setJobsCurrentPage] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsSearch, setJobsSearch] = useState('');

  // Pagination for pending customers
  const [customersCurrentPage, setCustomersCurrentPage] = useState(1);
  const [customersTotalPages, setCustomersTotalPages] = useState(1);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersSearch, setCustomersSearch] = useState('');

  const canQuery = startDate && endDate;

  const exportToCSV = (filename, rows, columns) => {
    try {
      const header = columns.map(c => '"' + c.header + '"').join(',');
      const body = rows.map(r => columns.map(c => '"' + (c.accessor(r) ?? '') + '"').join(',')).join('\n');
      const csv = header + '\n' + body;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('CSV export failed', e);
      toast.error('Export failed');
    }
  };

  const fetchAll = async () => {
    if (!canQuery) {
      toast.error('Select both start and end dates');
      return;
    }
    try {
      setLoading(true);
      const [sumRes, jobsRes, custRes] = await Promise.all([
        getReportsSummary({ startDate, endDate }),
        getPendingJobsReport({
          startDate,
          endDate,
          page: jobsCurrentPage,
          limit: 10,
          search: jobsSearch || undefined
        }),
        getPendingCustomersReport({
          startDate,
          endDate,
          page: customersCurrentPage,
          limit: 10,
          search: customersSearch || undefined
        })
      ]);
      setSummary(sumRes || { salesTotal: 0, pendingJobs: 0, pendingBalance: 0 });
      setPendingJobs(jobsRes?.jobs || []);
      setJobsTotal(jobsRes?.total || 0);
      setJobsTotalPages(jobsRes?.totalPages || 1);
      setPendingCustomers(custRes?.customers || []);
      setCustomersTotal(custRes?.total || 0);
      setCustomersTotalPages(custRes?.totalPages || 1);
    } catch (e) {
      console.error('Fetch reports failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to load reports'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-load last 7 days by default
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setStartDate(fmt(start));
    setEndDate(fmt(end));
  }, []);

  useEffect(() => {
    if (startDate && endDate) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Refetch when jobs pagination or search changes
  useEffect(() => {
    if (startDate && endDate) {
      setJobsCurrentPage(1); // Reset to first page when search changes
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsSearch]);

  useEffect(() => {
    if (startDate && endDate) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsCurrentPage]);

  // Refetch when customers pagination or search changes
  useEffect(() => {
    if (startDate && endDate) {
      setCustomersCurrentPage(1); // Reset to first page when search changes
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersSearch]);

  useEffect(() => {
    if (startDate && endDate) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersCurrentPage]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/dashboard" />
        <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div>
            <button className="btn-primary w-full" onClick={fetchAll} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-md border border-green-200">
            <div className="text-green-700 text-sm font-medium">Sales Total</div>
            <div className="text-2xl font-bold text-green-900 mt-1">KSh {Number(summary.salesTotal || 0).toLocaleString()}</div>
          </div>
          <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl shadow-md border border-orange-200">
            <div className="text-orange-700 text-sm font-medium">Pending Jobs</div>
            <div className="text-2xl font-bold text-orange-900 mt-1">{Number(summary.pendingJobs || 0)}</div>
          </div>
          <div className="p-4 bg-gradient-to-br from-red-50 to-rose-100 rounded-xl shadow-md border border-red-200">
            <div className="text-red-700 text-sm font-medium">Pending Balance</div>
            <div className="text-2xl font-bold text-red-900 mt-1">KSh {Number(summary.pendingBalance || 0).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Pending Jobs */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-orange-900">Pending Jobs</h3>
              <p className="text-sm text-orange-600">Jobs awaiting completion</p>
            </div>
            <button
              className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-white rounded-lg hover:bg-orange-50 shadow-sm border border-orange-200 transition-all"
              onClick={() => exportToCSV(
                `pending-jobs-${startDate}-to-${endDate}.csv`,
                pendingJobs,
                [
                  { header: 'Job ID', accessor: r => r.jobId },
                  { header: 'Customer', accessor: r => r.customer?.name },
                  { header: 'Phone', accessor: r => r.customer?.phone },
                  { header: 'Due Date', accessor: r => r.expectedDueDate ? new Date(r.expectedDueDate).toLocaleDateString() : '' },
                  { header: 'Status', accessor: r => r.status },
                  { header: 'Total', accessor: r => r.totalPrice.toLocaleString() },
                  { header: 'Advance', accessor: r => r.advancePayment.toLocaleString() },
                  { header: 'Collected', accessor: r => r.amountCollected.toLocaleString() },
                  { header: 'Balance Due', accessor: r => r.balanceDue.toLocaleString() },
                ]
              )}
            >
              Export CSV
            </button>
          </div>

          {/* Search for Pending Jobs */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search jobs by ID, customer name, or phone..."
              value={jobsSearch}
              onChange={(e) => setJobsSearch(e.target.value)}
              className="input-field w-full max-w-md"
            />
          </div>

          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-gradient-to-r from-orange-50 to-amber-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Job ID</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Advance</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Collected</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Balance Due</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pendingJobs.map((job, index) => (
                  <tr key={job._id} className={`hover:bg-orange-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.jobId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.customer?.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.customer?.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.expectedDueDate ? new Date(job.expectedDueDate).toLocaleDateString() : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.status}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(job.totalPrice || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(job.advancePayment || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(job.amountCollected || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(job.balanceDue || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && pendingJobs.length === 0 && <div className="text-center text-gray-500 py-6">No pending jobs</div>}

          {/* Pagination for Pending Jobs */}
          <Pagination
            currentPage={jobsCurrentPage}
            totalPages={jobsTotalPages}
            total={jobsTotal}
            pageSize={10}
            onPageChange={setJobsCurrentPage}
          />
        </div>
      </div>

      {/* Pending Customers */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-rose-200 bg-gradient-to-br from-rose-50 via-pink-50 to-red-50">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-rose-900">Customers with Pending Balances</h3>
              <p className="text-sm text-rose-600">Outstanding payments due</p>
            </div>
            <button
              className="px-3 py-1.5 text-sm font-medium text-rose-700 bg-white rounded-lg hover:bg-rose-50 shadow-sm border border-rose-200 transition-all"
              onClick={() => exportToCSV(
                `pending-customers-${startDate}-to-${endDate}.csv`,
                pendingCustomers,
                [
                  { header: 'Customer', accessor: r => r.customerName },
                  { header: 'Phone', accessor: r => r.phone },
                  { header: 'Pending Jobs', accessor: r => r.jobs },
                  { header: 'Total Balance', accessor: r => r.totalBalance },
                ]
              )}
            >
              Export CSV
            </button>
          </div>

          {/* Search for Pending Customers */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search customers by name or phone..."
              value={customersSearch}
              onChange={(e) => setCustomersSearch(e.target.value)}
              className="input-field w-full max-w-md"
            />
          </div>

          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-rose-100">
              <thead className="bg-gradient-to-r from-rose-50 to-pink-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-rose-800 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-rose-800 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-rose-800 uppercase tracking-wider">Pending Jobs</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-rose-800 uppercase tracking-wider">Total Balance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pendingCustomers.map((customer, index) => (
                  <tr key={customer._id} className={`hover:bg-rose-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.customerName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{customer.jobs}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(customer.totalBalance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && pendingCustomers.length === 0 && <div className="text-center text-gray-500 py-6">No pending customers</div>}

          {/* Pagination for Pending Customers */}
          <Pagination
            currentPage={customersCurrentPage}
            totalPages={customersTotalPages}
            total={customersTotal}
            pageSize={10}
            onPageChange={setCustomersCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Reports; 